<template>
  <div class="flowbuilder-node-container" ref="containerEl">
    <BaseNode
      ref="nodeEl"
      :node="node"
      @delete-node="handleDelete"
      classes="bg-emerald-100 dark:bg-emerald-950 ring-emerald-400/50 dark:ring-emerald-700/50"
      :icon="nodeIcon">
      <template #context>Condition check:</template>

      <div v-html="description"></div>

      <template #hover v-if="showConditionPreview && nodeProps.describeLogic">
        <ConditionPreview
          :condition="node.condition"
          :describe-logic="nodeProps.describeLogic"
          :context="logicContext" />
      </template>
    </BaseNode>

    <div
      class="flowbuilder-splits flowbuilder-splits-curve"
      ref="splitsEl"
      @click.stop>
      <div class="flowbuilder-split relative" ref="splitYesEl">
        <div class="flowbuilder-split-label" ref="yesEl">
          Yes
          <div class="flowbuilder-split-user-count" v-if="useEnhancedReporting">
            <UsersIcon class="h-2.5 w-2.5" />
            <span class="user-count-amount">{{ yesVisitorCount }}</span>
          </div>
        </div>

        <NodeRun
          :nodes="node.yes"
          :parent-node="node"
          :in-redirect-funnel="inRedirectFunnel"
          @nodes-updated="handleNodesUpdated('yes', $event)"
          @node-action="(...args) => handleNodeAction('yes', ...args)" />
      </div>
      <div class="flowbuilder-split relative" ref="splitNoEl">
        <div class="flowbuilder-split-label" ref="noEl">
          No
          <div class="flowbuilder-split-user-count" v-if="useEnhancedReporting">
            <UsersIcon class="h-2.5 w-2.5" />
            <span class="user-count-amount">{{ noVisitorCount }}</span>
          </div>
        </div>

        <NodeRun
          :nodes="node.no"
          :parent-node="node"
          :in-redirect-funnel="inRedirectFunnel"
          @nodes-updated="handleNodesUpdated('no', $event)"
          @node-action="(...args) => handleNodeAction('no', ...args)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, inject, ref, watch } from 'vue'
import type { Ref } from 'vue'
import BaseNode from './BaseNode.vue'
import ConditionPreview from './ConditionPreview.vue'
import NodeRun from './NodeRun.vue'
import { getNodeIcon } from '../../utils/nodeIcons'
import type { NodeYesNo } from '../../types/nodes'
import { UsersIcon } from '@heroicons/vue/24/outline'

/**
 * YesNoNode Component
 *
 * A conditional branching node that splits flow based on a logic condition.
 *
 * Features:
 * - Evaluates a condition and branches to "Yes" or "No" paths
 * - Supports custom note/description or auto-generated from condition
 * - Two child branches for binary decision flow
 *
 * Props:
 * - node: The yes/no node configuration with condition and branches
 * - icon: Optional custom icon
 * - FunnelIcon: Default icon component for conditional nodes
 */

interface Props {
  node: NodeYesNo
  icon?: any
  FunnelIcon: any
}

const props = defineProps<Props>()

const emit = defineEmits(['delete-node'])

const { node } = toRefs(props)

// Inject confirm dialog
const confirm = inject('confirm') as any

// Inject inRedirectFunnel to pass down to child NodeRun components
const inRedirectFunnel = inject('inRedirectFunnel', false)

// Check if enhanced reporting is enabled via adapter's global settings or injected settings
const adapter = inject('adapter') as any
const settings = inject<any>('settings', ref({}))
const useEnhancedReporting = computed(() => {
  // First check adapter's globalSettings if available
  if (adapter?.globalSettings?.value?.enhancedFlowReporting !== undefined) {
    return adapter.globalSettings.value.enhancedFlowReporting === true
  }
  // Fall back to injected settings
  return settings.value?.enhancedFlowReporting === true
})

// Define proper interface for nodeProps
interface NodeProps {
  describeLogic?: (condition: any, context?: any) => Promise<string | null>
}

// Inject dependencies from FlowBuilder
const nodeProps = inject<Ref<NodeProps>>('nodeProps', ref({} as NodeProps))
const preview = inject('preview', false)
const partners = inject('partners', ref([]))
const segmentGroups = inject('segmentGroups', ref([]))
const questions = inject('questions', ref([]))
const widgets = inject('widgets', ref([]))
const integrationDataVersion = inject('integrationDataVersion', ref(0))
const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)
// Inject getRawNodeMetrics function
const getRawNodeMetrics = inject<(node: any) => any>(
  'getRawNodeMetrics',
  () => null
)

// Track async logic description
const logicDescription = ref<string>('')

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // If an icon is passed as a prop, use it
  if (props.icon) return props.icon

  // Otherwise use the mapped icon
  return getNodeIcon(props.node)
})

// Get raw metrics for visitor counts
const rawMetrics = computed(() => {
  if (!getRawNodeMetrics || typeof getRawNodeMetrics !== 'function') {
    return null
  }
  const metrics = getRawNodeMetrics(props.node)
  return metrics
})

// Get visitor counts for each branch
const yesVisitorCount = computed(() => {
  // Use raw metrics to get branch distribution when enhanced reporting is enabled
  if (useEnhancedReporting.value && rawMetrics.value?.branchDistribution) {
    return rawMetrics.value.branchDistribution.yes || 0
  }
  // Fall back to formatted metrics for legacy
  const m = getNodeMetrics(props.node)
  if (m) return m.find((o: any) => o.label === 'Yes')?.value || 0
  return 0
})

const noVisitorCount = computed(() => {
  // Use raw metrics to get branch distribution when enhanced reporting is enabled
  if (useEnhancedReporting.value && rawMetrics.value?.branchDistribution) {
    return rawMetrics.value.branchDistribution.no || 0
  }
  // Fall back to formatted metrics for legacy
  const m = getNodeMetrics(props.node)
  if (m) return m.find((o: any) => o.label === 'No')?.value || 0
  return 0
})

const logicContext = computed(() => ({
  preview: preview,
  partners: partners.value,
  segmentGroups: segmentGroups.value,
  questions: questions.value,
  widgets: widgets.value
}))

const isLogicObject = (value: any): value is Record<string, any> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

const hasRealConditionLeaf = (condition: any): boolean => {
  if (!isLogicObject(condition)) return false

  if (Array.isArray(condition.and)) {
    return condition.and.some(hasRealConditionLeaf)
  }

  if (Array.isArray(condition.or)) {
    return condition.or.some(hasRealConditionLeaf)
  }

  return condition.$type !== undefined && String(condition.$type).length > 0
}

const showConditionPreview = computed(() => {
  return hasRealConditionLeaf(node.value.condition)
})

/**
 * Generate description for the condition
 * Uses custom note if provided, otherwise generates description from logic
 */
const description = computed(() => {
  // If there's a custom note, use that
  if (node.value.note) {
    return node.value.note
  }

  // If we have a logic description from async call, use that
  if (logicDescription.value) {
    // Process the description similar to legacy component - replace **text** with <strong>text</strong>
    return logicDescription.value.replace(
      /\*\*(.*?)\*\*/g,
      '<strong>$1</strong>'
    )
  }

  // Default message when no description is available
  return 'Condition needs setup'
})

/**
 * Generate human-readable description of the logic condition
 */
const generateLogicDescription = async () => {
  try {
    if (!node.value.condition || !nodeProps.value.describeLogic) {
      logicDescription.value = ''
      return
    }

    const result = await nodeProps.value.describeLogic(
      node.value.condition,
      logicContext.value
    )
    logicDescription.value = result || ''
  } catch (error) {
    console.warn('Failed to generate logic description:', error)
    logicDescription.value = 'Complex condition'
  }
}

// Watch for changes to the condition and regenerate description
watch(
  () => node.value.condition,
  () => {
    if (
      !node.value.note &&
      node.value.condition &&
      nodeProps.value.describeLogic
    ) {
      generateLogicDescription()
    }
  },
  { deep: true, immediate: true }
)

// Watch for changes to the note to clear logic description
watch(
  () => node.value.note,
  (newNote) => {
    if (newNote) {
      // Clear the async logic description when using a custom note
      logicDescription.value = ''
    } else if (node.value.condition && nodeProps.value.describeLogic) {
      // Regenerate logic description when note is cleared
      generateLogicDescription()
    }
  }
)

// Watch for integration data changes
watch(integrationDataVersion, () => {
  // Only regenerate if we're using auto-generated description (no custom note)
  if (
    !node.value.note &&
    node.value.condition &&
    nodeProps.value.describeLogic
  ) {
    generateLogicDescription()
  }
})

// Note: adapter already injected above for globalSettings access

// Handle nodes update from child NodeRun
const handleNodesUpdated = (branch: 'yes' | 'no', newNodes: any[]) => {
  node.value[branch] = newNodes
}

// Handle node actions from child NodeRun
const handleNodeAction = (
  branch: 'yes' | 'no',
  nodeIndex: number,
  action: string,
  data?: any
) => {
  const targetNode = node.value[branch][nodeIndex]
  if (!targetNode) return

  if (action === 'click' && adapter?.onNodeClick) {
    adapter.onNodeClick(targetNode)
  } else if (action === 'delete' && adapter?.onNodeDelete) {
    adapter.onNodeDelete(targetNode)
  }
}

// Check if there are any child nodes in either branch
const hasChildNodes = computed(() => {
  return (node.value.yes && node.value.yes.length > 0) ||
         (node.value.no && node.value.no.length > 0)
})

// Handle delete with confirmation if there are child nodes
const handleDelete = () => {
  if (!hasChildNodes.value) {
    emit('delete-node')
    return
  }

  if (!confirm) {
    // Fallback to native confirm if PrimeVue confirm not available
    if (window.confirm('This will remove all nodes in both the Yes and No paths. The actual questions, forms, and offers will not be deleted. Are you sure?')) {
      emit('delete-node')
    }
    return
  }

  confirm.require({
    header: 'Remove?',
    message: 'This will remove all nodes in both the Yes and No paths. The actual questions, forms, and offers will not be deleted.',
    acceptProps: { label: 'Remove', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: () => {
      emit('delete-node')
    }
  })
}
</script>
